// The pirate dens (v2-the-deep.md §8.5, R2720, ui-map-interface.md
// §6.1.13 "The dens"): the ten designs of mockup round 118, two for each
// brotherhood, in the six states of round 112. Card 02 is the Broken Ridge
// of round 130 (R2837).
//
// Each design is a real thing on the sea bed, seen from above in plan: a
// wreck, a ship or a shape of the terrain, in the NPC ink with the deep ink
// as its dark edge (§2.3, R1797). This module ports the drawing code of the
// page point for point. A design has three parts:
//
// - the BODY: everything that holds still. The layer paints it on a sheet
//   once for each state.
// - the MOVING part: a lantern that swings, a lead on its line, a loose
//   line, a trailing chain, the three chevrons of the current, a flag. The
//   page animates these and nothing else. Where the viewer asked for less
//   motion, or in a state where the part holds still, the layer paints it
//   with the body at the still pose of the page.
// - the TOP: the dashed track of a group at sea, the respawn figure of a
//   sunk group and the pirate cache mark of the holder of the chart
//   fragment. The page draws them over the den.
//
// The page also draws the group at its berths, the group on its roam ring,
// three wreck marks, the own hull and the sonar pulse of a SEARCH. The game
// does not: the fleet layer draws each hull the viewer hears, and the wreck
// piece of the chart draws each wreck. A den draws the den alone.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { ACCENT_COLOR, DETONATION_LIMITS, MARKER_NEUTRAL, NPC_INK } from '../gen/presentation';
import { NPC_DEN_CACHE_AT } from '../gen/worldgen';
import { rgba } from './color';
import { TAU } from './math';
import { Fl, disc, rot, stepPx, wordFont } from './seaobject-kit';

/** The context every painter takes. */
export type Ctx = CanvasRenderingContext2D;
/** A point, in map px. */
type Pt = readonly [number, number];
/** A box round the den centre, in map px: x0, y0, x1, y1. */
export type DenBox = readonly [number, number, number, number];

/** The three forms of a den: the group home, at sea, or sunk. */
export type DenForm = 'home' | 'sea' | 'sunk';
/** The pirate cache of the den for the holder of its chart fragment. */
export type DenCacheMark = 'none' | 'seen' | 'taken';

const PI = Math.PI;
/** The NPC ink and its dark edge (§2.3, R1797). */
const NPC = NPC_INK.color, DEEP = NPC_INK.edge, EDGE_A = NPC_INK.edgeAlpha;
/** The own ink of the cache mark (round 088 card 10, R2649). */
const ACCENT = ACCENT_COLOR;
/** The white core of a lit lantern. */
const WHITE = MARKER_NEUTRAL;
/** The water ink of the palette (#0a2739), the ground under a ledge. */
const WATER = DETONATION_LIMITS.water;

// ---- the kit of the page ------------------------------------------------------

const ink = (col: number, a: number): string => rgba(col, Math.max(0, Math.min(1, a)));
const NO_DASH: number[] = [];

function P(c: Ctx, pts: readonly Pt[], close = true): void {
  c.beginPath();
  for (let i = 0; i < pts.length; i++) {
    if (i) c.lineTo(pts[i][0], pts[i][1]); else c.moveTo(pts[i][0], pts[i][1]);
  }
  if (close) c.closePath();
}
function circle(c: Ctx, x: number, y: number, r: number, col: number, a: number, w = 1, dash?: number[]): void {
  c.beginPath(); c.arc(x, y, r, 0, TAU); c.lineWidth = w; c.strokeStyle = ink(col, a);
  c.setLineDash(dash ?? NO_DASH); c.stroke(); c.setLineDash(NO_DASH);
}
function line(c: Ctx, x0: number, y0: number, x1: number, y1: number, col: number, a: number, w = 1): void {
  c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.lineWidth = w;
  c.strokeStyle = ink(col, a); c.lineCap = 'round'; c.stroke();
}
function S(c: Ctx, col: number, a: number, w: number): void {
  c.lineWidth = w; c.strokeStyle = ink(col, a); c.lineJoin = 'round'; c.lineCap = 'round'; c.stroke();
}
/** The NPC ink over its dark edge. */
function edge(c: Ctx, w: number, a = 0.95, dash?: number[] | null): void {
  c.lineJoin = 'round'; c.lineCap = 'round'; c.setLineDash(dash ?? NO_DASH);
  c.lineWidth = w + 1.8; c.strokeStyle = ink(DEEP, EDGE_A); c.stroke();
  c.lineWidth = w; c.strokeStyle = ink(NPC, a); c.stroke(); c.setLineDash(NO_DASH);
}
function inkFill(c: Ctx, a = 0.95): void {
  c.fillStyle = ink(NPC, a); c.fill(); c.lineWidth = 1; c.lineJoin = 'round'; c.strokeStyle = ink(DEEP, EDGE_A); c.stroke();
}
function hollow(c: Ctx, w = 1.1, a = 0.9, dash?: number[]): void { c.fillStyle = ink(DEEP, 0.85); c.fill(); edge(c, w, a, dash); }
function arrow(c: Ctx, x: number, y: number, a: number, s: number): void {
  c.save(); c.translate(x, y); c.rotate(a);
  c.beginPath(); c.moveTo(-s * 0.6, -s); c.lineTo(s * 0.4, 0); c.lineTo(-s * 0.6, s); edge(c, 1.2, 0.85);
  c.restore();
}
/** The beacon blink of §6.2. */
function blink(t: number): number { return 0.4 + 0.6 * Math.abs(Math.sin(2 * t)); }

/** The look of one state: solid at home and at sea, dashed and dim when the group is sunk. */
interface Look { al: number; dash: number[] | null }
const SOLID: Look = { al: 0.92, dash: null };
const DASHED: Look = { al: 0.55, dash: [3, 3] };
function look(f: DenForm): Look { return f === 'sunk' ? DASHED : SOLID; }

// ---- the real things the dens are made of ------------------------------------

/** A ship hull in plan, bow east, length L, beam W. `blunt` gives the square bow of a hauler. */
function hullPath(c: Ctx, L: number, W: number, blunt: boolean): void {
  const h = L / 2, b = W / 2; c.beginPath();
  if (blunt) {
    c.moveTo(h, 0); c.quadraticCurveTo(h, -b, h - b * 1.3, -b); c.lineTo(-h + 2.5, -b); c.lineTo(-h, -b + 2.5);
    c.lineTo(-h, b - 2.5); c.lineTo(-h + 2.5, b); c.lineTo(h - b * 1.3, b); c.quadraticCurveTo(h, b, h, 0);
  } else {
    c.moveTo(h, 0); c.quadraticCurveTo(h * 0.5, -b, h * 0.1, -b); c.lineTo(-h + b, -b); c.quadraticCurveTo(-h, -b, -h, 0);
    c.quadraticCurveTo(-h, b, -h + b, b); c.lineTo(h * 0.1, b); c.quadraticCurveTo(h * 0.5, b, h, 0);
  }
  c.closePath();
}
/** The scour hollow that the current digs round a wreck on the sea bed. */
function scour(c: Ctx, L: number, W: number): void {
  c.beginPath(); c.ellipse(0, 0, L / 2 + 9, W / 2 + 8, 0, 0, TAU); Fl(c, DEEP, 0.38);
}
function denHull(c: Ctx, L: number, W: number, blunt: boolean, f: DenForm): void {
  const k = look(f);
  hullPath(c, L, W, blunt); Fl(c, DEEP, 0.78); hullPath(c, L, W, blunt); edge(c, 1.2, k.al, k.dash);
}
/** The ribs of a broken deck, across the hull. */
function ribs(c: Ctx, L: number, W: number, f: DenForm, step = 5): void {
  const k = look(f), b = W / 2 - 1.2; c.beginPath();
  for (let x = -L / 2 + 7; x < L / 2 - 9; x += step) { c.moveTo(x, -b); c.lineTo(x + 1.2, b); }
  edge(c, 0.55, k.al * 0.55);
}
/** A chain: a row of small links along a polyline. */
function chain(c: Ctx, pts: readonly Pt[], a = 0.85): void {
  c.beginPath();
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], q = pts[i], L = Math.hypot(q[0] - p[0], q[1] - p[1]);
    for (let s = 0; s < L; s += 2.6) {
      const x = p[0] + (q[0] - p[0]) * s / L, y = p[1] + (q[1] - p[1]) * s / L;
      c.moveTo(x + 1, y); c.arc(x, y, 1, 0, TAU);
    }
  }
  c.lineWidth = 2.4; c.strokeStyle = ink(DEEP, EDGE_A); c.stroke();
  c.lineWidth = 0.75; c.strokeStyle = ink(NPC, a); c.stroke();
}
/** A rope or a line: one thin stroke with its dark edge. */
function rope(c: Ctx, pts: readonly Pt[], a = 0.8): void { P(c, pts, false); edge(c, 0.7, a); }
/** A rock in plan: a dark mass with the NPC edge. */
function rock(c: Ctx, pts: readonly Pt[], f: DenForm): void {
  const k = look(f); P(c, pts); Fl(c, DEEP, 0.88); P(c, pts); edge(c, 1.3, k.al, k.dash);
}
/** Hachures along a polyline, on the side of the normal (dy, -dx): down the slope. */
function hach(c: Ctx, pts: readonly Pt[], len: number, a: number, sign = 1): void {
  c.beginPath();
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], q = pts[i], dx = q[0] - p[0], dy = q[1] - p[1], L = Math.hypot(dx, dy);
    const nx = sign * dy / L, ny = -sign * dx / L;
    for (let s = 2.5; s < L; s += 5) {
      const x = p[0] + dx * s / L, y = p[1] + dy * s / L; c.moveTo(x, y); c.lineTo(x + nx * len, y + ny * len);
    }
  }
  edge(c, 0.6, a);
}
/** The lantern of the Lantern Kin: a glass diamond with a lit core and a glow. */
function lantern(c: Ctx, x: number, y: number, f: DenForm, t: number, m: boolean): void {
  if (f === 'home') {
    const a = m ? 0.55 + 0.45 * blink(t * 0.35) : 1;
    disc(c, x, y, 15, NPC, 0.1 * a); disc(c, x, y, 7.5, NPC, 0.16 * a);
  }
  line(c, x, y - 3.8, x, y - 5.6, NPC, f === 'sunk' ? 0.4 : 0.8, 1);
  const b = scratch(4);
  put(b, 0, x, y - 3.8); put(b, 1, x + 2.8, y); put(b, 2, x, y + 3.8); put(b, 3, x - 2.8, y);
  P(c, b);
  if (f === 'home') { inkFill(c); disc(c, x, y, 1.2, WHITE, 1); } else if (f === 'sea') hollow(c, 1.1); else hollow(c, 0.9, 0.5, [1.5, 1.5]);
}
/** A grapnel in plan: a shaft with a ring at its end and two hooks that turn back. */
function grapnel(c: Ctx, x: number, y: number, a: number, s: number, al: number): void {
  c.save(); c.translate(x, y); c.rotate(a); c.scale(s, s);
  c.beginPath(); c.moveTo(-7, 0); c.lineTo(1, 0);
  for (const k of [-1, 1]) { c.moveTo(1, 0); c.quadraticCurveTo(4.5, k * 1.2, 3.6, k * 4.6); c.lineTo(1.4, k * 3.4); }
  c.moveTo(-7, 0); c.arc(-8.6, 0, 1.6, 0, TAU);
  edge(c, 1.1 / s, al); c.restore();
}
const LEAD: readonly Pt[] = [[-1.4, 0], [1.4, 0], [2.4, 7], [-2.4, 7]];
/** A sounding lead: a long weight in the NPC ink, pointing down its line. */
function leadAt(c: Ctx, x: number, y: number, a: number, f: DenForm): void {
  c.save(); c.translate(x, y); c.rotate(a - PI / 2);
  P(c, LEAD);
  if (f === 'sunk') hollow(c, 0.9, 0.5, [1.5, 1.5]); else inkFill(c);
  c.restore();
}
/** A line with a knot every 6 px: the marks of a sounding line. */
function knotLine(c: Ctx, x0: number, y0: number, x1: number, y1: number): void {
  rope(c, seg(x0, y0, x1, y1), 0.85);
  const L = Math.hypot(x1 - x0, y1 - y0), nx = -(y1 - y0) / L, ny = (x1 - x0) / L;
  c.beginPath();
  for (let s = 6; s < L - 2; s += 6) {
    const x = x0 + (x1 - x0) * s / L, y = y0 + (y1 - y0) * s / L;
    c.moveTo(x - nx * 1.6, y - ny * 1.6); c.lineTo(x + nx * 1.6, y + ny * 1.6);
  }
  edge(c, 0.7, 0.85);
}

// ---- the ten designs ------------------------------------------------------------

/** One design of round 118. */
export interface DenDesign {
  /** The card number of round 118, 1 to 10. */
  card: number;
  /** The name of `[[npc.brotherhoods]] dens`, and the key of its word. */
  key: string;
  /** The brotherhood, as the wire enum `Brotherhood` (1 to 5). */
  bro: number;
  /** The track of the group at sea: start, control and end. */
  out: readonly [number, number, number, number, number, number];
  /** Where the respawn figure and the cache mark stand. The cache mark
   *  reads `cache_at` of `[[npc.brotherhoods]]`, the point where the hub
   *  places the pirate cache (R2859). */
  figAt: Pt;
  cacheAt: Pt;
  /** The extent of the body, and of the moving part. */
  box: DenBox;
  mbox: DenBox | null;
  /** The forms whose moving part moves with the clock. */
  moves: readonly DenForm[];
  body(c: Ctx, f: DenForm): void;
  /** The moving part at the clock `t`; `m` false paints the still pose. */
  moving?(c: Ctx, f: DenForm, t: number, m: boolean): void;
}

// The polygons of the page.
const JAW: readonly Pt[] = [[-42, -18], [-22, -32], [4, -34], [28, -26], [44, -12], [50, 0], [44, 12], [30, 28], [8, 36], [-16, 34], [-40, 22],
  [-38, 14], [-14, 18], [10, 18], [30, 10], [36, 0], [30, -10], [10, -16], [-14, -16], [-40, -10]];
const JAW_UP: readonly Pt[] = [[-30, -12.3], [-18, -15], [-6, -16], [6, -16], [18, -13.6]];
const JAW_DOWN: readonly Pt[] = [[-24, 16.3], [-12, 18], [0, 18], [12, 17.2], [22, 13.2]];
// The Broken Ridge of round 130 card 02 (R2837): two long slabs on one diagonal, and a crest line along each.
const RIDGE_W: readonly Pt[] = [[-66, 16], [-52, 6], [-40, 4], [-28, -4], [-18, -6], [-10, -10], [-6, -4], [-12, 4], [-24, 8], [-34, 16], [-48, 20], [-60, 28], [-68, 24]];
const RIDGE_E: readonly Pt[] = [[12, -16], [22, -22], [34, -26], [44, -34], [58, -38], [64, -32], [56, -24], [44, -20], [32, -12], [20, -6], [14, -4], [9, -9]];
const CREST_W: readonly Pt[] = [[-62, 20], [-46, 12], [-30, 6], [-14, -2]];
const CREST_E: readonly Pt[] = [[14, -10], [30, -18], [46, -27], [60, -32]];
const LEDGE_RIM: readonly Pt[] = [[-46, -4], [-24, -1], [0, -3], [24, 0], [46, -3]];
const LEDGE_LIP: readonly Pt[] = [[46, 20], [24, 24], [8, 22], [-10, 25], [-30, 21], [-46, 22]];
const FUNNEL_W: readonly Pt[] = [[-50, -30], [-38, -33], [-22, -13], [-8, 14], [-4, 24], [-11, 25], [-19, 11], [-35, -9]];
const FUNNEL_E: readonly Pt[] = FUNNEL_W.map((p) => [-p[0], p[1]] as Pt);
const SPUR_ROCK: readonly Pt[] = [[30, 22], [38, 16], [46, 20], [46, 30], [36, 34], [28, 30]];

/** The rock spur of Hook Spur: the centre line of the C, widened by its half width. */
const SPUR: { outer: readonly Pt[]; outline: readonly Pt[] } = (() => {
  const C: readonly Pt[] = [[10, 32], [-12, 33], [-32, 26], [-40, 8], [-34, -12], [-16, -24], [6, -26], [22, -18], [30, -8], [28, -1]];
  const HW = [6, 6.5, 6.5, 6.5, 6.5, 6.5, 6, 5, 3.5, 2];
  const outer: Pt[] = [], inner: Pt[] = [];
  C.forEach((p, i) => {
    const a = C[Math.max(0, i - 1)], b = C[Math.min(C.length - 1, i + 1)];
    const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy), nx = dy / L, ny = -dx / L;
    outer.push([p[0] + nx * HW[i], p[1] + ny * HW[i]]); inner.push([p[0] - nx * HW[i], p[1] - ny * HW[i]]);
  });
  return { outer, outline: outer.concat(inner.reverse()) };
})();

/** The grapnels of the Grappled Hauler along its deck, and the berths
 *  where their lines run at home. */
const HAULER_GRAPNELS = [-24, 2, 26] as const;
const HAULER_BERTHS: readonly Pt[] = [[-24, -38], [2, -40], [26, -38]];

/** The top of the lantern pole of the Lantern Wreck, and the two ends of the
 *  survey ship's line, home and at sea: fixed, so a frame turns no point. */
const LANTERN_TOP = rot([-10, -38], -0.3);
const SURVEY_HOME = rot([8, -26], 0.12);
const SURVEY_SEA = rot([26, -4], 0.12);

/** Scratch polylines of the moving parts, one for each length, so a frame
 *  allocates no point (§10). A painter fills one and draws it before it
 *  fills the next. */
const SCRATCH: [number, number][][] = [];
function scratch(n: number): [number, number][] {
  let b = SCRATCH[n];
  if (!b) { b = []; for (let i = 0; i < n; i++) b.push([0, 0]); SCRATCH[n] = b; }
  return b;
}
function put(b: [number, number][], i: number, x: number, y: number): void { b[i][0] = x; b[i][1] = y; }
/** The two-point scratch line from (x0, y0) to (x1, y1). */
function seg(x0: number, y0: number, x1: number, y1: number): readonly Pt[] {
  const b = scratch(2); put(b, 0, x0, y0); put(b, 1, x1, y1); return b;
}

/** The place of the pirate cache of a design: the brotherhood (1 to 5) and
 *  the index into its `dens` list, from the data layer (R2859). */
function cacheAtOf(bro: number, design: number): Pt {
  return NPC_DEN_CACHE_AT[bro - 1]?.[design] ?? [0, 0];
}

/** The ten designs in the card order of round 118. */
export const DEN_DESIGNS: readonly DenDesign[] = [
  { card: 1, key: 'lantern_wreck', bro: 1, out: [-2, -12, 0, -44, 22, -62], figAt: [-23, 7], cacheAt: cacheAtOf(1, 0),
    box: [-46, -40, 46, 24], mbox: [-40, -40, -2, -6], moves: ['home', 'sea'],
    body(c, f) {
      const k = look(f), A = -0.3;
      c.save(); c.rotate(A);
      scour(c, 70, 18);
      c.beginPath(); c.moveTo(-2, -8); c.lineTo(-10, -38); edge(c, 1.6, k.al, k.dash);
      c.beginPath(); c.moveTo(-18, -30); c.lineTo(-3, -33.5); edge(c, 1, k.al * 0.85, k.dash);
      rope(c, [[-22, -8], [-10, -38], [16, -8]], k.al * 0.5);
      denHull(c, 70, 18, false, f); ribs(c, 70, 18, f);
      c.beginPath(); c.rect(-1, -3.5, 8, 7); Fl(c, DEEP, 0.9); c.beginPath(); c.rect(-1, -3.5, 8, 7); edge(c, 0.7, k.al * 0.8);
      for (const p of [[19, 0], [-20, 0]] as const) {
        c.beginPath(); c.arc(p[0], p[1], 1.8, 0, TAU); if (f === 'sunk') hollow(c, 0.8, 0.5); else inkFill(c);
      }
      c.restore();
      if (f === 'sunk') lantern(c, LANTERN_TOP[0] + 7, LANTERN_TOP[1] + 5, f, 0, false);
    },
    moving(c, f, t, m) {
      if (f === 'sunk') return;
      const top = LANTERN_TOP, sw = m ? Math.sin(t * 0.7) * 2.2 : 1.2;
      rope(c, seg(top[0], top[1], top[0] + sw, top[1] + 7), 0.75);
      lantern(c, top[0] + sw, top[1] + 10.5, f, t, m);
    } },
  { card: 2, key: 'broken_ridge', bro: 1, out: [1, -2, 6, -40, 30, -60], figAt: [2, 18], cacheAt: cacheAtOf(1, 1),
    box: [-73, -43, 69, 33], mbox: [-17, -17, 19, 17], moves: ['home', 'sea'],
    body(c, f) {
      const k = look(f);
      rock(c, RIDGE_W, f); rock(c, RIDGE_E, f);
      hach(c, RIDGE_W.concat([RIDGE_W[0]]), 3.5, k.al * 0.5); hach(c, RIDGE_E.concat([RIDGE_E[0]]), 3.5, k.al * 0.5);
      rope(c, CREST_W, k.al * 0.35); rope(c, CREST_E, k.al * 0.35);
      if (f === 'sunk') { chain(c, [[-8, -6], [-3, -2]], 0.55); lantern(c, 4, 2, f, 0, false); }
    },
    moving(c, f, t, m) {
      if (f === 'sunk') return;
      const sw = m ? Math.sin(t * 0.6) * 1.5 : 1;
      const b = scratch(3);
      put(b, 0, -8, -6); put(b, 1, 1 + sw * 0.5, -4 + sw * 0.3); put(b, 2, 10, -11);
      chain(c, b, 0.85);
      lantern(c, 1 + sw * 0.5, sw * 0.3, f, t, m);
    } },
  { card: 3, key: 'jaw_cut', bro: 2, out: [-40, 0, -64, -4, -86, -20], figAt: [20, 1], cacheAt: cacheAtOf(2, 0),
    box: [-48, -40, 56, 42], mbox: null, moves: [],
    body(c, f) {
      const k = look(f);
      rock(c, JAW, f);
      hach(c, JAW.slice(0, 11), 4, k.al * 0.5);
      for (const p of JAW_UP) {
        P(c, [[p[0] - 3, p[1]], [p[0], p[1] + 5], [p[0] + 3, p[1]]]); if (f === 'sunk') hollow(c, 0.8, 0.5, [1.5, 1.5]); else inkFill(c);
      }
      for (const p of JAW_DOWN) {
        P(c, [[p[0] - 3, p[1]], [p[0], p[1] - 5], [p[0] + 3, p[1]]]); if (f === 'sunk') hollow(c, 0.8, 0.5, [1.5, 1.5]); else inkFill(c);
      }
    } },
  { card: 4, key: 'rammed_pair', bro: 2, out: [-30, 0, -56, 2, -78, 18], figAt: [36, -22], cacheAt: cacheAtOf(2, 1),
    box: [-34, -40, 54, 40], mbox: null, moves: [],
    body(c, f) {
      c.save(); c.translate(10, 0); c.beginPath(); c.ellipse(0, 0, 40, 34, 0, 0, TAU); Fl(c, DEEP, 0.34); c.restore();
      for (const h of [[0.55, 1], [-0.55, -1]] as const) {
        const a = h[0], cx = 34 - 27 * Math.cos(a), cy = -27 * Math.sin(a);
        c.save(); c.translate(cx, cy); c.rotate(a);
        denHull(c, 54, 13, false, f); ribs(c, 54, 13, f, 6);
        const k = look(f), side = h[1] * 6.5;
        for (const x of [-18, -10, -2, 6]) {
          P(c, [[x - 1.6, side], [x + 0.4, side + h[1] * 4], [x + 1.6, side]]); if (f === 'sunk') hollow(c, 0.7, 0.5); else inkFill(c, k.al);
        }
        c.restore();
      }
    } },
  { card: 5, key: 'sounding_ledge', bro: 3, out: [-20, 22, -40, 40, -60, 60], figAt: [-12, 10], cacheAt: cacheAtOf(3, 0),
    box: [-50, -12, 50, 60], mbox: [6, 18, 40, 56], moves: ['home'],
    body(c, f) {
      const k = look(f);
      P(c, LEDGE_RIM.concat(LEDGE_LIP)); Fl(c, WATER, 0.95);
      P(c, LEDGE_RIM, false); edge(c, 1.2, k.al, k.dash); hach(c, LEDGE_RIM, 3.5, k.al * 0.5, -1);
      P(c, LEDGE_LIP, false); edge(c, 1.3, k.al, k.dash); hach(c, LEDGE_LIP, 5, k.al * 0.55);
      P(c, [[9, 23.5], [12, 18], [15, 23.5]]); if (f === 'sunk') hollow(c, 0.8, 0.5); else inkFill(c);
      if (f === 'sunk') leadAt(c, 12 + 20, 23 + 26, 0, f);
    },
    moving(c, f, t, m) {
      if (f === 'sunk') return;
      const sx = 12, sy = 23;
      const L = f === 'home' ? 26 : 5, sw = f === 'home' && m ? Math.sin(t * 0.5) * 3 : 0;
      const ex = sx + L * 0.62 + sw, ey = sy + L * 0.78;
      knotLine(c, sx, sy, ex, ey);
      leadAt(c, ex, ey, Math.atan2(ey - sy, ex - sx), f);
    } },
  { card: 6, key: 'survey_ship', bro: 3, out: [12, 14, 40, 40, 72, 52], figAt: [-20, 0], cacheAt: cacheAtOf(3, 1),
    box: [-50, -34, 50, 24], mbox: [6, -30, 36, -10], moves: ['home'],
    body(c, f) {
      const k = look(f), A = 0.12;
      c.save(); c.rotate(A);
      scour(c, 72, 12);
      denHull(c, 72, 12, false, f);
      c.beginPath(); c.moveTo(-30, 0); c.lineTo(30, 0); edge(c, 0.5, k.al * 0.45);
      c.beginPath(); c.rect(-27, -4, 11, 8); Fl(c, DEEP, 0.9); c.beginPath(); c.rect(-27, -4, 11, 8); edge(c, 0.9, k.al, k.dash);
      c.beginPath(); c.arc(4, 0, 3, 0, TAU); if (f === 'sunk') hollow(c, 0.8, 0.5); else inkFill(c, k.al);
      if (f === 'home') { c.beginPath(); c.moveTo(8, -4); c.lineTo(8, -26); edge(c, 1.2, k.al); }
      else if (f === 'sea') { c.beginPath(); c.moveTo(8, -4); c.lineTo(26, -4); edge(c, 1.2, k.al); }
      else {
        c.beginPath(); c.moveTo(8, -4); c.lineTo(8, -10); edge(c, 1.2, k.al, k.dash);
        c.beginPath(); c.moveTo(3, -16); c.lineTo(12, -27); edge(c, 1.1, k.al, k.dash);
      }
      c.restore();
      if (f === 'sunk') leadAt(c, 20, -28, 0.6, f);
    },
    moving(c, f, t, m) {
      if (f === 'sunk') return;
      const p = f === 'home' ? SURVEY_HOME : SURVEY_SEA;
      if (f === 'home') {
        const sw = m ? Math.sin(t * 0.55) * 2.5 : 0, ex = p[0] + 15 + sw, ey = p[1] + 7;
        knotLine(c, p[0], p[1], ex, ey); leadAt(c, ex, ey, Math.atan2(ey - p[1], ex - p[0]), f);
      } else leadAt(c, p[0] + 1, p[1] + 2.5, PI / 2 - 0.2, f);
    } },
  { card: 7, key: 'grappled_hauler', bro: 4, out: [0, -30, 10, -80, 40, -112], figAt: [-12, 0], cacheAt: cacheAtOf(4, 0),
    box: [-48, -42, 48, 22], mbox: [-47, -31, 29, -17], moves: ['sea'],
    body(c, f) {
      const k = look(f);
      scour(c, 66, 18);
      denHull(c, 66, 18, true, f);
      for (const x of [-22, -10, 2, 14]) {
        c.beginPath(); c.rect(x, -5, 8, 10); Fl(c, DEEP, 0.9); c.beginPath(); c.rect(x, -5, 8, 10); edge(c, 0.7, k.al * 0.8, k.dash);
      }
      for (let i = 0; i < HAULER_GRAPNELS.length; i++) {
        const x = HAULER_GRAPNELS[i], ry = -20.6;
        grapnel(c, x, -12, PI / 2, 1, f === 'sunk' ? 0.55 : 0.92);
        if (f === 'home') { const b = HAULER_BERTHS[i]; rope(c, [[x, ry], [b[0] - 6, b[1] + 3]], 0.8); }
        else if (f === 'sunk') rope(c, [[x, ry], [x - 1, ry - 4]], 0.55);
      }
    },
    moving(c, f, t, m) {
      if (f !== 'sea') return;
      for (let i = 0; i < HAULER_GRAPNELS.length; i++) {
        const x = HAULER_GRAPNELS[i], pts = scratch(6);
        for (let j = 0; j <= 5; j++) put(pts, j, x - j * 4, -20.6 - j * 1.2 + (m ? Math.sin(t * 1.3 + j * 0.9 + i) : Math.sin(j)) * 1.6);
        rope(c, pts, 0.7);
      }
    } },
  { card: 8, key: 'hook_spur', bro: 4, out: [32, 8, 58, -12, 74, -52], figAt: [-8, 3], cacheAt: cacheAtOf(4, 1),
    box: [-50, -36, 50, 42], mbox: [33, 2, 61, 22], moves: ['sea'],
    body(c, f) {
      const k = look(f);
      rock(c, SPUR.outline, f); hach(c, SPUR.outer, 3.5, k.al * 0.5);
      rock(c, SPUR_ROCK, f);
      grapnel(c, 26, -3, -0.9, 0.8, k.al);
      if (f === 'home') chain(c, [[29, 1], [32, 10], [34, 18]], 0.88);
      else if (f === 'sunk') { chain(c, [[29, 1], [30.5, 5]], 0.5); chain(c, [[34, 18], [33.5, 14]], 0.5); }
    },
    moving(c, f, t, m) {
      if (f !== 'sea') return;
      const w = m ? Math.sin(t * 1.1) * 1.5 : 0;
      const b = scratch(4);
      put(b, 0, 36, 18); put(b, 1, 44, 12 + w); put(b, 2, 52, 8 - w); put(b, 3, 58, 6 + w);
      chain(c, b, 0.8);
    } },
  { card: 9, key: 'down_funnel', bro: 5, out: [0, -46, -10, -76, -50, -96], figAt: [0, 4], cacheAt: cacheAtOf(5, 0),
    box: [-54, -38, 54, 38], mbox: [-29, -33, 29, 26], moves: ['home', 'sea', 'sunk'],
    body(c, f) {
      const k = look(f);
      c.beginPath(); c.moveTo(-38, -33); c.lineTo(38, -33); c.lineTo(4, 24); c.lineTo(-4, 24); c.closePath(); Fl(c, DEEP, 0.5);
      rock(c, FUNNEL_W, f); rock(c, FUNNEL_E, f);
      hach(c, [[-38, -33], [-22, -13], [-8, 14], [-4, 24]], 3.5, k.al * 0.5);
      hach(c, [[38, -33], [22, -13], [8, 14], [4, 24]], 3.5, k.al * 0.5, -1);
      c.beginPath(); c.ellipse(0, 29, 7, 5, 0, 0, TAU); Fl(c, DEEP, 1);
      c.beginPath(); c.ellipse(0, 29, 7, 5, 0, 0, TAU); edge(c, 1, k.al, k.dash);
    },
    moving(c, f, t, m) {
      for (let j = 0; j < 3; j++) {
        const q = m ? ((t * 0.12 + j / 3) % 1) : (0.15 + j * 0.3), y = -24 + q * 44, hw = 26 - q * 20;
        const a = (f === 'sunk' ? 0.35 : 0.8) * (1 - q * 0.8);
        const b = scratch(3);
        put(b, 0, -hw, y - 6); put(b, 1, 0, y + 3); put(b, 2, hw, y - 6);
        P(c, b, false); edge(c, 1.3, a);
      }
    } },
  { card: 10, key: 'sunken_dreadnought', bro: 5, out: [0, -24, 30, -60, 70, -72], figAt: [-22, 3], cacheAt: cacheAtOf(5, 1),
    box: [-56, -26, 64, 44], mbox: [-4, -2, 10, 15], moves: ['home'],
    body(c, f) {
      const k = look(f), A = -0.1;
      for (const v of [[0, 22, 30], [0, 31, 24], [0, 40, 18]] as const) {
        rope(c, [[v[0] - v[2], v[1] - 7], [v[0], v[1]], [v[0] + v[2], v[1] - 7]], k.al * 0.5);
      }
      c.save(); c.rotate(A);
      scour(c, 86, 20);
      chain(c, [[43, -2], [52, -6], [60, -6]], k.al * 0.8);
      denHull(c, 86, 20, false, f);
      c.beginPath(); c.moveTo(-38, 0); c.lineTo(38, 0); edge(c, 0.45, k.al * 0.4);
      c.beginPath(); c.rect(-6, -6, 16, 12); Fl(c, DEEP, 0.9); c.beginPath(); c.rect(-6, -6, 16, 12); edge(c, 1, k.al, k.dash);
      for (const p of [[30, 1], [18, 1], [-28, -1]] as const) {
        const dir = p[0] > 0 ? 1 : -1;
        c.beginPath(); c.roundRect(p[0] - 3.5, -3.5, 7, 7, 1.5); if (f === 'sunk') hollow(c, 0.8, 0.5); else inkFill(c, k.al);
        line(c, p[0] + dir * 3.5, -1.3, p[0] + dir * 9, -1.3, NPC, k.al * 0.8, 0.9);
        line(c, p[0] + dir * 3.5, 1.3, p[0] + dir * 9, 1.3, NPC, k.al * 0.8, 0.9);
      }
      disc(c, 2, 0, 1.6, NPC, k.al);
      if (f === 'sunk') {
        P(c, [[7, -3], [17, -3], [14, -0.5], [17, 2], [7, 2]]); hollow(c, 0.8, 0.5, [1.5, 1.5]);
      }
      c.restore();
    },
    moving(c, f, t, m) {
      if (f !== 'home') return;
      const w = m ? Math.sin(t * 1.6) * 1.4 : 0.6;
      c.save(); c.rotate(-0.1);
      const b = scratch(5);
      put(b, 0, 0, 1.5); put(b, 1, 4, 1.5); put(b, 2, 4.5 + w, 12); put(b, 3, 2 + w * 0.5, 8.5); put(b, 4, -0.5 + w, 12);
      P(c, b); inkFill(c);
      c.restore();
    } },
];

/** The design of a den: the wire's brotherhood (1 to 5) and the index into
 *  its `dens` list (0 or 1), in the card order of round 118. Null for a
 *  pair the ten designs do not hold. */
export function denDesignOf(bro: number, design: number): DenDesign | null {
  if (!(bro >= 1 && bro <= 5) || !(design === 0 || design === 1)) return null;
  return DEN_DESIGNS[(bro - 1) * 2 + design];
}

/** True when the moving part of a design moves with the clock in a form. */
export function denMoves(d: DenDesign, f: DenForm): boolean {
  return d.moving !== undefined && d.moves.includes(f);
}

// ---- the top: the track, the respawn figure, the cache mark ----------------------------

/** The cache mark of round 088 card 10 (R2649) in the own ink: a cross in a
 *  dotted ring. A taken cache closes its ring and shows a tick. */
export function paintCacheMark(c: Ctx, x: number, y: number, taken: boolean): void {
  c.save(); c.translate(x, y);
  disc(c, 0, 0, 10.5, DEEP, 0.6);
  if (!taken) {
    circle(c, 0, 0, 10, ACCENT, 0.9, 1.3, [1.5, 2.5]);
    line(c, -5.5, -5.5, 5.5, 5.5, ACCENT, 0.95, 1.9); line(c, 5.5, -5.5, -5.5, 5.5, ACCENT, 0.95, 1.9);
  } else {
    circle(c, 0, 0, 10, ACCENT, 0.5, 1.2);
    P(c, [[-5, 0], [-1.5, 4], [5.5, -4.5]], false); S(c, ACCENT, 0.65, 1.9);
  }
  c.restore();
}

/** The size of the respawn figure, map px: the label step, in its plate,
 *  so it grows with the water as the plate does (R3249). */
const FIG_PX = stepPx('mono', 'label');

/** The respawn figure: the span with its units in a dark plate (R1125, R2741). */
function figPlate(c: Ctx, s: string, x: number, y: number, face: string): void {
  c.font = wordFont('mono', FIG_PX, face);
  const w = c.measureText(s).width;
  c.beginPath(); c.roundRect(x - w / 2 - 3, y - 6.5, w + 6, 13, 2); c.fillStyle = ink(DEEP, 0.88); c.fill();
  c.lineWidth = 0.8; c.strokeStyle = ink(NPC, 0.5); c.stroke();
  c.fillStyle = ink(NPC, 0.95); c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText(s, x, y + 0.5);
}

/** What the top of a den draws. */
export interface DenTop {
  form: DenForm;
  cache: DenCacheMark;
  /** The respawn figure of a sunk group, or '' for none. */
  fig: string;
  /** The family of the figure. */
  face: string;
  /** The point of the cache mark round the den origin, from the chart
   *  fragment, or null for the point of the design. The two differ only
   *  where the hub brought the cache back into the den cell (R2859). */
  cacheAt: Pt | null;
}

/** The top of a den at its origin: the track at sea, the respawn figure
 *  when the group is sunk, and the cache mark of the fragment holder. */
export function paintDenTop(c: Ctx, d: DenDesign, top: DenTop): void {
  if (top.form === 'sea') {
    const q = d.out;
    c.beginPath(); c.moveTo(q[0], q[1]); c.quadraticCurveTo(q[2], q[3], q[4], q[5]); edge(c, 1, 0.6, [3, 4]);
    arrow(c, q[4], q[5], Math.atan2(q[5] - q[3], q[4] - q[2]), 4.5);
  }
  if (top.form === 'sunk' && top.fig) figPlate(c, top.fig, d.figAt[0], d.figAt[1], top.face);
  if (top.cache !== 'none') {
    const at = top.cacheAt ?? d.cacheAt;
    paintCacheMark(c, at[0], at[1], top.cache === 'taken');
  }
}

/** The box of the top of a den in a form, round its origin: the track at
 *  sea, the respawn figure when the group is sunk, the cache mark. */
function denTopBox(d: DenDesign, form: DenForm, cache: DenCacheMark): DenBox | null {
  let b: DenBox | null = null;
  if (form === 'sea') {
    const q = d.out;
    b = unionBox(b, [Math.min(q[0], q[2], q[4]) - 6, Math.min(q[1], q[3], q[5]) - 6,
      Math.max(q[0], q[2], q[4]) + 6, Math.max(q[1], q[3], q[5]) + 6]);
  }
  if (form === 'sunk') b = unionBox(b, [d.figAt[0] - 22, d.figAt[1] - 8, d.figAt[0] + 22, d.figAt[1] + 8]);
  if (cache !== 'none') b = unionBox(b, [d.cacheAt[0] - 12, d.cacheAt[1] - 12, d.cacheAt[0] + 12, d.cacheAt[1] + 12]);
  return b;
}

/** The two boxes a sheet of a den covers, round its origin. */
export interface DenBoxes {
  /** The whole den: the body, the moving part and the top. */
  full: DenBox;
  /** The moving part and the top, or null for a design with no moving part. */
  motion: DenBox | null;
}

const FORMS: readonly DenForm[] = ['home', 'sea', 'sunk'];
const CACHES: readonly DenCacheMark[] = ['none', 'seen', 'taken'];
const BOXES = new Map<DenDesign, DenBoxes[]>();

/** The boxes of a den in a form with a cache mark, computed once. */
export function denBoxes(d: DenDesign, form: DenForm, cache: DenCacheMark): DenBoxes {
  let all = BOXES.get(d);
  if (!all) {
    all = [];
    for (const f of FORMS) {
      for (const k of CACHES) {
        const top = denTopBox(d, f, k);
        all.push({ full: unionBox(unionBox(d.box, d.mbox), top)!, motion: d.mbox ? unionBox(d.mbox, top) : null });
      }
    }
    BOXES.set(d, all);
  }
  return all[FORMS.indexOf(form) * 3 + CACHES.indexOf(cache)];
}

/** The box of the lone cache mark, round its origin. */
export const CACHE_MARK_BOX: DenBox = [-12, -12, 12, 12];

/** The union of two boxes; null is the empty box. */
export function unionBox(a: DenBox | null, b: DenBox | null): DenBox | null {
  if (!a) return b;
  if (!b) return a;
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])];
}
